import React, { useRef, useState, useEffect } from "react";
import { Grid, Typography, Button, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const steps = [
    {
      number: "1",
      title: "Establish Your Foundation",
      description: "Build secure and scalable microservice ecosystems effortlessly.",
    },
    {
      number: "2",
      title: "Automate Workflows",
      description: "Tailor CI/CD pipelines and operational tasks to your organizational needs.",
    },
    {
      number: "3",
      title: "Enable Auto Scalability",
      description: "Adapt dynamically to workload changes, ensuring high availability and reliability.",
    },
];

// Animated step component
const AnimatedStep = ({ step, index }) => {
    const [isVisible, setIsVisible] = useState(false);
    const stepRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.unobserve(entry.target);
                }
            },
            { threshold: 0.3 } // Trigger when 30% of the element is visible
        );

        if (stepRef.current) {
            observer.observe(stepRef.current);
        }

        return () => {
            if (stepRef.current) {
                observer.unobserve(stepRef.current);
            }
        };
    }, []);

    // Calculate animation delay based on step index
    const getAnimationDelay = () => {
        return 0.2 + (index * 0.2); // Base delay + staggered delay
    };

    return (
        <Grid
            item
            xs={12}
            sm={4}
            ref={stepRef}
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                zIndex: "2"
            }}
        >
            {/* Animated number circle */}
            <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ 
                    scale: isVisible ? 1 : 0, 
                    opacity: isVisible ? 1 : 0 
                }}
                transition={{ 
                    duration: 0.5, 
                    delay: getAnimationDelay(),
                    type: "spring",
                    stiffness: 200
                }}
            >
                <Box
                    sx={{
                        width: "50px",
                        height: "50px",
                        backgroundColor: "#FAD32E",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 3,
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{ 
                            fontWeight: "bold", 
                            color: "#000000",
                            fontFamily: "Poppins sans-serif",
                        }}
                    >
                        {step.number}
                    </Typography>
                </Box>
            </motion.div>

            {/* Animated title */}
            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ 
                    y: isVisible ? 0 : 20, 
                    opacity: isVisible ? 1 : 0 
                }}
                transition={{ 
                    duration: 0.6, 
                    delay: getAnimationDelay() + 0.2
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: "semibold",
                        color: "#FAD32E",
                        mb: 2,
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "20px",
                    }}
                >
                    {step.title}
                </Typography>
            </motion.div>

            {/* Animated description */}
            <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ 
                    y: isVisible ? 0 : 20, 
                    opacity: isVisible ? 1 : 0 
                }}
                transition={{ 
                    duration: 0.6, 
                    delay: getAnimationDelay() + 0.4
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        color: "#dcdcdc",
                        maxWidth: "300px",
                        mb: 3,
                        fontFamily: "Poppins, sans-serif",
                        textAlign: "center",
                    }}
                >
                    {step.description}
                </Typography>
            </motion.div>
        </Grid>
    );
};

const Section4 = () => {
    const navigate = useNavigate();
    const [headingVisible, setHeadingVisible] = useState(false);
    const [lineVisible, setLineVisible] = useState(false);
    const [buttonVisible, setButtonVisible] = useState(false);
    const headingRef = useRef(null);
    const lineRef = useRef(null);
    const buttonRef = useRef(null);

    useEffect(() => {
        const headingObserver = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setHeadingVisible(true);
                    headingObserver.unobserve(entry.target);
                }
            },
            { threshold: 0.1 }
        );

        const lineObserver = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setLineVisible(true);
                    lineObserver.unobserve(entry.target);
                }
            },
            { threshold: 0.1 }
        );

        const buttonObserver = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setButtonVisible(true);
                    buttonObserver.unobserve(entry.target);
                }
            },
            { threshold: 0.1 }
        );

        if (headingRef.current) {
            headingObserver.observe(headingRef.current);
        }

        if (lineRef.current) {
            lineObserver.observe(lineRef.current);
        }

        if (buttonRef.current) {
            buttonObserver.observe(buttonRef.current);
        }

        return () => {
            if (headingRef.current) {
                headingObserver.unobserve(headingRef.current);
            }
            if (lineRef.current) {
                lineObserver.unobserve(lineRef.current);
            }
            if (buttonRef.current) {
                buttonObserver.unobserve(buttonRef.current);
            }
        };
    }, []);
    
    return (
        <Box
            sx={{
                height: "100%",
                py: 10,
                backgroundColor: "#000000",
                textAlign: "center",
                justifyContent: "center",
                alignContent: "center",
                alignItems: "center",
            }}
        >
            {/* Animated heading */}
            <motion.div
                ref={headingRef}
                initial={{ y: -30, opacity: 0 }}
                animate={{ 
                    y: headingVisible ? 0 : -30, 
                    opacity: headingVisible ? 1 : 0 
                }}
                transition={{ duration: 0.7 }}
            >
                <Typography
                    variant="h4"
                    sx={{
                        color: "#FAD32E",
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: "medium",
                        mb: 4,
                        fontSize: "25px",
                    }}
                >
                    BUILT FOR SCALABLE MICRO SERVICES
                </Typography>
            </motion.div>
            
            {/* Animated subheading */}
            <motion.div
                initial={{ y: -20, opacity: 0 }}
                animate={{ 
                    y: headingVisible ? 0 : -20, 
                    opacity: headingVisible ? 1 : 0 
                }}
                transition={{ duration: 0.7, delay: 0.2 }}
            >
                <Typography
                    sx={{
                        color: "#FFFFFF",
                        fontFamily: "Poppins, sans-serif ",
                        fontSize: "18px",
                        mb: 6,
                        paddingLeft: "10%",
                        paddingRight: "10%",
                        fontWeight: "medium",
                        marginTop: "-10px"
                    }}
                >
                    Hashinfra makes microservices management easier enabling businesses adopt modern DevOps methods and reach their full potential.
                </Typography>
            </motion.div>
            
            <Grid 
                container 
                spacing={4} 
                justifyContent="center"
                sx={{ position: "relative", px: 4 }}
            >
                {/* Animated line under the number boxes */}
                <Box
                    ref={lineRef}
                    sx={{
                        position: "absolute",
                        top: "55px",
                        left: "10%",
                        width: "80%",
                        height: "2px",
                        backgroundImage: "linear-gradient(270deg, #FAD32E, #000000)",
                        borderRadius: "50%",
                        zIndex: 0,
                        opacity: lineVisible ? 1 : 0,
                        transition: "opacity 1s ease-in-out",
                        // Hide line on mobile
                        "@media (max-width: 600px)": {
                            display: "none",
                        }
                    }}
                />

                {/* Animated steps */}
                {steps.map((step, index) => (
                    <AnimatedStep key={index} step={step} index={index} />
                ))}
            </Grid>

            {/* Animated button */}
            <Box mt={4} ref={buttonRef}>
                <motion.div
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ 
                        y: buttonVisible ? 0 : 30, 
                        opacity: buttonVisible ? 1 : 0 
                    }}
                    transition={{ duration: 0.7, delay: 0.8 }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    <Button
                        variant="outlined"
                        sx={{
                            borderColor: "#FAD32E",
                            color: "#FAD32E",
                            fontFamily: "Poppins, sans-serif",
                            px: 3,
                            py: 1,
                            borderRadius: "14px",
                            "&:hover": {
                                backgroundColor: "#FAD32E",
                                color: "#000000",
                            },
                        }}
                        onClick={() => {
                            navigate('/Form');
                        }}>
                        See Hashinfra in Action
                    </Button>
                </motion.div>
            </Box>
        </Box>
    );
};

export default Section4;

// import React from "react";
// import { Grid, Typography, Button, Box } from "@mui/material";
// import { useNavigate } from "react-router-dom";

// const steps = [
//     {
//       number: "1",
//       title: "Establish Your Foundation",
//       description: "Build secure and scalable microservice ecosystems effortlessly.",
//     },
//     {
//       number: "2",
//       title: "Automate Workflows",
//       description: "Tailor CI/CD pipelines and operational tasks to your organizational needs.",
//     },
//     {
//       number: "3",
//       title: "Enable Auto Scalability",
//       description: "Adapt dynamically to workload changes, ensuring high availability and reliability.",
//     },
// ];


// const Section4 = () => {

//     const navigate = useNavigate();
    
//     return (
//         <Box
//             sx={{
//                 height: "100%",
//                 py: 10,
//                 backgroundColor: "#000000",
//                 textAlign: "center",
//                 justifyContent: "center",
//                 alignContent: "center",
//                 alignItems: "center",
//             }}
//         >
//             <Typography
//                 variant="h4"
//                 sx={{
//                     color: "#FAD32E",
//                     fontFamily: "Poppins, sans-serif",
//                     fontWeight: "medium",
//                     mb: 4,
//                     fontSize: "25px",
//                 }}
//             >
//                 BUILT FOR SCALABLE MICRO SERVICES
//             </Typography>
            
//             <Typography
//                 sx={{
//                     color: "#FFFFFF",
//                     fontFamily: "Poppins, sans-serif ",
//                     fontSize: "18px",
//                     mb: 6,
//                     paddingLeft: "10%",
//                     paddingRight: "10%",
//                     fontWeight:"medium",
//                     marginTop:"-10px"
//                 }}
//             >
//                 Hashinfra makes microservices management easier enabling businesses adopt modern DevOps methods and reach their full potential.
//             </Typography>
            
//             <Grid 
//                 container 
//                 spacing={4} 
//                 justifyContent="center"
//                 sx={{ position: "relative", px: 4 }}
//                 >
//                     {/* Line under the number boxes */}
//                     <Box
//                         sx={{
//                             position: "absolute",
//                             top: "20px", // Adjust to align below numbers
//                             left: "10%",
//                             width: "80%",
//                             height: "2px",
//                             backgroundImage: "linear-gradient(270deg, #FAD32E, #000000)",
//                             borderRadius: "50%",
//                             zIndex: 0,
    
//                             // Hide line on mobile
//                             "@media (max-width: 600px)": {
//                                 display: "none",
//                             }
//                         }}
//                     />


//                 {steps.map((step, index) => (
//                     <Grid
//                         item
//                         xs={12}
//                         sm={4}
//                         key={index}
//                         sx={{
//                             display: "flex",
//                             flexDirection: "column",
//                             alignItems: "center",
//                             textAlign: "center",
//                             zIndex:"2"
//                         }}
//                     >
//                         <Box
//                             sx={{
//                                 width: "50px",
//                                 height: "50px",
//                                 backgroundColor: "#FAD32E",
//                                 borderRadius: "50%",
//                                 display: "flex",
//                                 alignItems: "center",
//                                 justifyContent: "center",
//                                 mb: 3,
//                             }}
//                         >
//                             <Typography
//                                 variant="h6"
//                                 sx={{ 
//                                     fontWeight: "bold", 
//                                     color: "#000000" ,
//                                     fontFamily: "Poppins sans-serif",
//                                 }}
//                             >
//                                 {step.number}
//                             </Typography>
//                         </Box>

//                         <Typography
//                             variant="h6"
//                             sx={{
//                                 fontWeight: "semibold",
//                                 color: "#FAD32E",
//                                 mb: 2,
//                                 fontFamily: "Poppins, sans-serif",
//                                 fontSize: "20px",
//                             }}
//                         >
//                             {step.title}
//                         </Typography>

//                         <Typography
//                             variant="body2"
//                             sx={{
//                                 color: "#dcdcdc",
//                                 maxWidth: "300px",
//                                 mb: 3,
//                                 fontFamily: "Poppins, sans-serif",
//                                 textAlign: "center",
//                             }}
//                         >
//                             {step.description}
//                         </Typography>
//                     </Grid>
//                 ))}
//             </Grid>

//             <Box mt={4}>
//                 <Button
//                     variant="outlined"
//                     sx={{
//                         borderColor: "#FAD32E",
//                         color: "#FAD32E",
//                         fontFamily: "Poppins, sans-serif",
//                         px: 3,
//                         py: 1,
//                         borderRadius: "14px",
//                         "&:hover": {
//                             backgroundColor: "#FAD32E",
//                             color: "#000000",
//                         },
//                     }}
//                     onClick={() => {
//                         window.open("https://calendly.com/clockhash/talk-to-us", "_blank");
//                       }}
//                 >
//                     See Hashinfra in Action
//                 </Button>
//             </Box>
//         </Box>
//     );
// };

// export default Section4;